import VibBreadcrumbs from '@vibTheme/components/VibBreadcrumbs';
import AffiliationIcon from '@mui/icons-material/Loyalty';
import AffiliationMarketIcon from '@mui/icons-material/Storefront';
import { useEffect, useState } from 'react';
import {
  AffiliationConfigModes,
  AffiliationMarketProduct,
} from '@interfaces/affiliationConfig';
import { findMarketProduct } from '@api/affiliationConfig';
import useToastContext from '@contexts/ToastContext/hook';
import { Typography } from '@vibTheme/components/_base/Typography';
import { VibSpinner } from '@vibTheme/components/VibSpinner';
import { AffiliationMarketProductPage } from '@components/AffiliationMarket/AffiliationMarketProductPage';
import { useRouter } from 'next/router';
import { useFeatureToggleAccess } from '@hooks/useFeatureToggleAccess';
import { useSession } from 'next-auth/react';
import { VibLoadingOverlay } from '@vibTheme/components/VibLoadingOverlay';

const breadcrumbs = [
  {
    label: 'Afiliações',
    icon: <AffiliationIcon />,
  },
  {
    label: 'Mercado de Afiliações',
    icon: <AffiliationMarketIcon />,
    href: '/affiliation/affiliationMarket',
  },
];

export default function MarketProductPage() {
  const [marketProduct, setMarketProduct] =
    useState<AffiliationMarketProduct>();
  const [isLoading, setIsLoading] = useState(true);
  const toast = useToastContext();

  const router = useRouter();
  const { data } = useSession();

  const {
    isFeatureEnabled: isAffiliationEnabled,
    isFeatureLoading: isAffiliationLoading,
  } = useFeatureToggleAccess({
    name: 'affiliation',
    valuesToValidate: { platformUserId: Number(data?.user?.id) },
  });
  const {
    isFeatureEnabled: isAffiliationMarketEnabled,
    isFeatureLoading: isAffiliationMarketLoading,
  } = useFeatureToggleAccess({
    name: 'affiliationMarket',
    valuesToValidate: { platformUserId: Number(data?.user?.id) },
  });
  const isFeatureLoading = isAffiliationLoading || isAffiliationMarketLoading;
  const isFeatureEnabled = isAffiliationEnabled && isAffiliationMarketEnabled;

  useEffect(() => {
    !isFeatureLoading && !isFeatureEnabled && router.push('/404');
  }, [isFeatureEnabled, isFeatureLoading]);

  const productCode = (router.query.productCode ?? '') as string;

  const handleRedirect = () => {
    toast.setContent(
      'A comunidade requisitada não existe ou não está participando do Mercado de Afiliações.',
      'error',
    );
    router.push('/affiliation/affiliationMarket');
  };

  const fetchMarketProduct = async () => {
    setIsLoading(true);

    try {
      const data = await findMarketProduct(productCode);
      data.mode === AffiliationConfigModes['Não liberado'] && handleRedirect();
      setMarketProduct(data);
    } catch (err: any) {
      if (err.statusCode === 404) {
        handleRedirect();
        return;
      }

      toast.handleError(err, 'Ocorreu um erro ao buscar a comunidade');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    isFeatureEnabled && fetchMarketProduct();
  }, [isFeatureEnabled]);

  const shouldRenderLoading =
    !isFeatureEnabled ||
    !marketProduct ||
    marketProduct?.mode === AffiliationConfigModes['Não liberado'];

  if (shouldRenderLoading) return <VibLoadingOverlay />;

  return (
    <>
      <VibBreadcrumbs items={breadcrumbs} />
      {isLoading && <VibSpinner className="my-main" />}
      {marketProduct && (
        <>
          <Typography component="h1" className="divider">
            {marketProduct.name}{' '}
            {marketProduct.sellerName && ` - ${marketProduct.sellerName}`}
          </Typography>

          <AffiliationMarketProductPage product={marketProduct} />
        </>
      )}
    </>
  );
}
